import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import Loading from '../common/Loading';
import { useMessage} from '../../contexts/MessageContext';
// ❌ REMOVE THIS LINE: import { Input } from 'postcss';

const apiUrl = window._env_?.REACT_APP_API_URL || 'https://app.mylocaleventrentals.com/api';

function formatOrderLabel(order) {
  if (!order) return 'View order';
  if (order.formattedOrderNumber) return order.formattedOrderNumber;
  if (order.orderNumber != null) return `ORD-${String(order.orderNumber).padStart(6, '0')}`;
  return 'View order';
}

function normalizePaymentFields(d) {
  if (!d) return { amount: '', method: '', date: '', status: '', invoice_id: '' };
  const dateVal = d.date ? String(d.date).slice(0, 10) : '';
  return {
    amount: d.amount != null && d.amount !== '' ? String(d.amount) : '',
    method: d.method || '',
    date: dateVal || new Date().toISOString().split('T')[0],
    status: d.status || '',
    invoice_id: d.invoice_id || '',
  };
}

const PaymentForm = () => {
  const [payment, setPayment] = useState({
    amount: '',
    method: '',
    date: new Date().toISOString().split('T')[0],
    status: '',
    invoice_id: '',
  });
  const [linkedInvoice, setLinkedInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showSuccess, showError } = useMessage();
  const { uuid } = useParams();
  const isEditMode = !!uuid;
  const invoiceIdFromQuery = searchParams.get('invoiceId');
  
  const paymentMethods = [
    { value: 'credit_card', label: 'Credit Card' },
    { value: 'bank_transfer', label: 'Bank Transfer' },
    { value: 'cash', label: 'Cash' },
  ];

   const paymentStatus = [
    { value: 'unpaid', label: 'Unpaid' },
    { value: 'paid', label: 'Paid' },
    { value: 'partial', label: 'Partial' },
    { value: 'refunded', label: 'Refunded' },
    { value: 'completed', label: 'Completed' }
  ];

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      logout();
      navigate('/login');
      return;
    }

    if (isEditMode) {
      const fetchPayments = async () => {
        try {
          const response = await axios.get(`${apiUrl}/payment/${uuid}`, {
            headers: { 'x-auth-token': token }
          });
          const raw = response.data?.data;
          const { invoice, ...rest } = raw || {};
          setPayment(normalizePaymentFields(rest));
          if (invoice) {
            setLinkedInvoice(invoice);
          } else if (raw?.invoice_id) {
            try {
              const invRes = await axios.get(`${apiUrl}/invoice/${raw.invoice_id}`, {
                headers: { 'x-auth-token': token },
              });
              if (invRes.data?.data) setLinkedInvoice(invRes.data.data);
            } catch (_) {
              /* optional enrichment */
            }
          }
        } catch (error) {
          console.error("Error fetching payments:", error);
          if(error.response && error.response.status === 401){
            showError("Unauthorized. Please login again.");
            logout();
            navigate('/login');
            return
          } else {
            showError("Failed to fetch payments");
          }
        } finally {
          setLoading(false);
        }
      };

      fetchPayments();
    } else if (invoiceIdFromQuery) {
      const loadInvoiceContext = async () => {
        try {
          const [invRes, payRes] = await Promise.all([
            axios.get(`${apiUrl}/invoice/${invoiceIdFromQuery}`, {
              headers: { 'x-auth-token': token },
            }),
            axios.get(`${apiUrl}/payment/invoice/${invoiceIdFromQuery}/payments`, {
              headers: { 'x-auth-token': token },
            }),
          ]);
          const inv = invRes.data?.data;
          const payList = payRes.data?.data || [];
          if (!inv) {
            showError('Invoice not found');
            setLoading(false);
            return;
          }
          setLinkedInvoice(inv);
          const total = parseFloat(inv.total_amount) || 0;
          const counts = (p) =>
            ['completed', 'paid'].includes(String(p.status || '').toLowerCase());
          const paid = payList.filter(counts).reduce((s, p) => s + parseFloat(p.amount || 0), 0);
          const balance = Math.max(0, total - paid);
          setPayment((prev) => ({
            ...prev,
            invoice_id: invoiceIdFromQuery,
            amount: balance > 0 ? balance.toFixed(2) : total.toFixed(2),
            status: 'completed',
          }));
        } catch (error) {
          console.error('Error loading invoice for payment:', error);
          showError(error.response?.data?.message || 'Failed to load invoice');
        } finally {
          setLoading(false);
        }
      };
      loadInvoiceContext();
    } else {
      setLoading(false);
    }
  }, [logout, navigate, isEditMode, uuid, showError, invoiceIdFromQuery]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    const amountNum = parseFloat(payment.amount);
    if (Number.isNaN(amountNum) || amountNum <= 0) {
      showError('Enter a valid payment amount');
      return;
    }

    try {
      if (isEditMode) {
        const updatePayload = {
          amount: amountNum,
          method: payment.method,
          date: payment.date,
          status: payment.status,
          ...(payment.invoice_id ? { invoice_id: payment.invoice_id } : {}),
          ...(payment.notes ? { notes: payment.notes } : {}),
        };
        await axios.put(`${apiUrl}/payment/${uuid}`, updatePayload, {
          headers: { 'x-auth-token': token }
        });
        showSuccess("Payment updated successfully!");
      } else {
        const payload = {
          ...payment,
          amount: amountNum,
        };
        if (!payload.invoice_id) delete payload.invoice_id;
        await axios.post(`${apiUrl}/payment`, payload, {
          headers: { 'x-auth-token': token }
        });
        showSuccess("Payment created successfully!");
        if (invoiceIdFromQuery) {
          navigate(`/invoice/${invoiceIdFromQuery}`);
        } else {
          navigate('/payment');
        }
        return;
      }
      navigate('/payment');
    } catch (error) {
      console.error("Error submitting payment:", error);
      showError("Failed to submit payment");
    }
  };

  if(loading){
    return <Loading />;
  }

  return (
    <div className="p-6 bg-white rounded shadow-md">
      <div className="max-w-3xl mx-auto border shadow-md p-4">
      <h2 className="text-2xl font-bold mb-4">
        {isEditMode ? "Edit Payment" : "Create Payment"}
      </h2>
      {linkedInvoice && (
        <div className="mb-4 p-4 rounded-md border border-blue-200 bg-blue-50 text-sm text-gray-800">
          <p className="font-semibold text-blue-900 mb-1">
            {isEditMode ? 'Linked records' : 'Linked invoice'}
          </p>
          <p>
            Invoice #{linkedInvoice.invoiceNumber ?? '—'} —{' '}
            <Link
              to={`/invoice/${linkedInvoice.uuid}`}
              className="text-blue-700 underline hover:text-blue-900"
            >
              Open invoice
            </Link>
          </p>
          {linkedInvoice.Customer && (
            <p className="mt-1 text-gray-600">
              {linkedInvoice.Customer.firstname} {linkedInvoice.Customer.lastname}
            </p>
          )}
          {(linkedInvoice.Order || linkedInvoice.order) && (
            <p className="mt-2">
              <span className="text-gray-600">Order:</span>{' '}
              <Link
                to={`/orders/${(linkedInvoice.Order || linkedInvoice.order).uuid}`}
                className="text-blue-700 underline hover:text-blue-900"
              >
                {formatOrderLabel(linkedInvoice.Order || linkedInvoice.order)}
              </Link>
            </p>
          )}
          <p className="mt-1">
            Invoice total:{' '}
            <span className="font-medium">${parseFloat(linkedInvoice.total_amount || 0).toFixed(2)}</span>
            {!isEditMode && invoiceIdFromQuery && payment.amount && (
              <>
                {' '}
                · Suggested amount (balance):{' '}
                <span className="font-medium">${parseFloat(payment.amount || 0).toFixed(2)}</span>
              </>
            )}
          </p>
        </div>
      )}
      <div className="mb-4">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Payment Method</label>
            <select
              className="w-full px-3 py-2 border rounded-md"
              value={payment.method}
              onChange={(e) => setPayment({ ...payment, method: e.target.value })}
              required
            >
              <option value="">Select a method</option>
              {paymentMethods.map((method) => (
                <option key={method.value} value={method.value}>
                  {method.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Amount</label>
            <input
              type="number"
              step="0.01"
              className="w-full px-3 py-2 border rounded-md"
              placeholder="0.00"
              value={payment.amount}
              onChange={(e) => setPayment({ ...payment, amount: e.target.value })}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Date</label>
            <input
              type="date"
              className="w-full px-3 py-2 border rounded-md"
              value={payment.date}
              onChange={(e) => setPayment({ ...payment, date: e.target.value })}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Payment Status</label>
            <select
              className="w-full px-3 py-2 border rounded-md"
              value={payment.status}
              onChange={(e) => setPayment({ ...payment, status: e.target.value })}
              required
            >
              <option value="">Select a status</option>
              {paymentStatus.map((status) => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
          </div>

          <button className='btn-primary' type="submit" disabled={isEditMode && loading}>
            {isEditMode ? 'Update Payment' : 'Submit Payment'}
          </button>
          <button
            type="button"
            className="ml-4 px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400"
            onClick={() => navigate('/payment')}
          >
            Cancel
          </button>
        </form>
      </div>
      </div>
    </div>
  );
};

export default PaymentForm;